<template>
    <div class="container">
        <FlashMessage/>
        <div class="row">
            <div class="sidebar" :class="{ 'sidebar-female' : $backendConfig.settings.sex === 'female' }">
                <div class="language-switch">
                    <a v-for="(link, locale) in $backendConfig.settings.languageSwitchLinks" class="language-choice"
                       :class="{ active: $backendConfig.settings.language === locale }"
                       rel="alternate"
                       :key="locale"
                       :hreflang="locale"
                       :href="link"
                    >
                        {{ locale }}
                    </a>
                </div>
                <a :href="appUrl" class="logo">
                    <img :src="$backendConfig.settings.brandLogoUrl" alt="Bespoken Brothers – It's personal" width="140" height="89">
                </a>
                <div class="configurator-desc">
                    <template v-if="$backendConfig.settings.sex == 'female'">
                        <div class="configurator-sex">{{ $tr.title.female }}</div>
                        <img :src="$imageUrl('cat-icon-ladies.svg')" alt="" title="" width="60">
                    </template>
                    <template v-else>
                        <div class="configurator-sex">{{ $tr.title.male }}</div>
                        <img :src="$imageUrl('cat-icon-gentlemen.svg')" alt="" title="" width="60">
                    </template>
                </div>

                <SideNav :products="products" @goToStep="goToStep" :activeIndex="currentStep"/>
            </div>
            <div class="mainbar">
                <Modal v-model="showRestoreConfigurationModal">
                    <h3>
                        {{ $tr.restoreConfigModal.title }}
                    </h3>
                    <p>{{ $tr.restoreConfigModal.desc }}</p>
                    <div class="mb-2">
                        <!-- Go to first product that is not selected -->
                        <Button type="black" @click="restoreConfiguration(); showRestoreConfigurationModal = false">
                            {{ $tr.restoreConfigModal.btnRestore }}
                        </Button>
                    </div>
                    <div class="mb-2" v-if="$cartConfigurations.length">
                        <Button type="white" @click="pickedEditCartShirt(); showRestoreConfigurationModal = false">
                            {{ $tr.cartEditModal.btnCartEdit }}
                        </Button>
                    </div>
                    <div class="mb-2">
                        <Button type="white" @click="currentStep = 0; showRestoreConfigurationModal = false">
                            {{ $tr.restoreConfigModal.btnAgain }}
                        </Button>
                    </div>
                    <div class="mb-2" v-if="$backendConfig.settings.displayUpselling">
                        <Button type="white" @click="currentStep = totalStepsNumber - 1; showRestoreConfigurationModal = false">
                            {{ $tr.restoreConfigModal.differentProduct }}
                        </Button>
                    </div>
                </Modal>

                <Modal v-model="showCartEditModal">
                    <p>{{ $tr.cartEditModal.desc }}</p>
                    <div class="mb-2">
                        <Button type="black" @click="pickedEditCartShirt(); showCartEditModal = false">
                            {{ $tr.cartEditModal.btnCartEdit }}
                        </Button>
                    </div>
                    <div class="mb-2">
                        <Button type="white" @click="currentStep = 0; showCartEditModal = false">
                            {{ $tr.cartEditModal.btnAgain }}
                        </Button>
                    </div>
                    <div class="mb-2" v-if="$backendConfig.settings.displayUpselling">
                        <Button type="white" @click="currentStep = totalStepsNumber - 1; showCartEditModal = false">
                            {{ $tr.restoreConfigModal.differentProduct }}
                        </Button>
                    </div>
                </Modal>

                <Modal v-model="showPickCartConfigurationModal" class="show-pick-cart-configuration-modal">
                    <p class="mb-4">
                        {{ $tr.pickCartConfigurationModal.title }}
                    </p>
                    <div class="mb-2"
                        v-for="config in $cartConfigurations">
                        <a :href="config.url" class="mt-0">
                            <Button type="black">
                                {{ config.name }}
                            </Button>
                        </a>
                    </div>
                    <div>
                        <Button type="white" @click="showPickCartConfigurationModal = false">
                            {{ $tr.pickCartConfigurationModal.cancel }}
                        </Button>
                    </div>
                </Modal>

                <Modal v-model="showFieldsInvalidModal">
                    <h3>
                        {{ $tr.invalidFieldModal.title }}
                    </h3>
                    <p>
                        {{ $tr.invalidFieldModal.desc }}
                    </p>
                </Modal>

                <Modal v-model="showProductsModal" @close="selectDefaults" class="bigger-modal">
                    <div style="height: 60px; visiblity: hidden"></div> <!-- modal top padding fix -->
                    <h3>
                        {{ $tr.productsModal.title }}
                    </h3>
                    <ul>
                        <template v-for="product in notSelectedProducts">
                            <a href="#" class="button white-btn centered-btn modal-link-btn"
                               @click.prevent="showProductsModal = false; goToStep(product.step)">
                                {{ product.name }}
                            </a>
                        </template>
                    </ul>
                    <p>
                        {{ $tr.productsModal.desc }}
                    </p>
                    <div class="mb-2">
                        <!-- Go to first product that is not selected -->
                        <Button @click="showProductsModal = false; goToStep(notSelectedProducts[0].step)"
                                type="go-back">
                            {{ $tr.productsModal.editBtn }}
                        </Button>
                    </div>
                    <div>
                        <Button @click="showProductsModal = false; selectDefaults()" type="black">
                            {{ $tr.productsModal.defaultsBtn }}
                        </Button>
                    </div>
                    <div style="height: 40px; visiblity: hidden"></div> <!-- modal bottom padding fix -->
                </Modal>

                <Progress :current="progressBarCurrentStep" :total="progressBarTotalSteps" :phases="generalData.phases"
                          class="d-none d-lg-flex progress-wrapper-row"/>

                <ProductPickerPage
                    v-for="(product, id, index) in products"
                    :key="product.id"
                    v-if="index === currentStep"
                    :product="product"
                    :secondProduct="getSecondProduct(product.secondChoice)"
                    :productIndex="index"
                    :totalSteps="Object.keys(products).length + 1"
                    @goToStep="goToStep"
                >
                    <template v-slot:progress>
                        <Progress :current="progressBarCurrentStep" :total="progressBarTotalSteps"
                                  :phases="generalData.phases"/>
                    </template>
                </ProductPickerPage>

                <MeasurementsPage v-if="currentMeasurementStep === 0"
                                  :baseCategories="measurements.categories.base"
                                  @goToStep="goToStep"
                >
                    <template v-slot:progress>
                        <Progress :current="progressBarCurrentStep" :total="progressBarTotalSteps"
                                  :phases="generalData.phases"/>
                    </template>
                </MeasurementsPage>

                <SummaryPage v-if="currentMeasurementStep === 1" @goToStep="goToStep"
                             @addedToCart="configurationFinished"
                             :categories="measurementsCategories" :measurementsBeginStep="Object.keys(products).length"
                             :shirtConfigured="!notSelectedProducts.length"
                >
                    <template v-slot:progress>
                        <Progress :current="progressBarCurrentStep" :total="progressBarTotalSteps"
                                  :phases="generalData.phases"/>
                    </template>
                </SummaryPage>

            </div>
        </div>
    </div>
</template>

<script>
import SideNav from "./SideNav";
import Progress from "./Progress";

import ProductPickerPage from "./ProductPickerPage";

import MeasurementsPage from "./MeasurementsPage";
import SummaryPage from "./SummaryPage";

import Modal from "./common/Modal";
import Button from "./common/Button";

import {EventBus} from "../event-bus";
import FlashMessage from "./FlashMessage";

export default {
    name: "Root",
    components: {
        FlashMessage,
        SideNav,
        Progress,
        ProductPickerPage,
        MeasurementsPage,
        SummaryPage,
        Modal,
        Button
    },
    data() {
        return {
            generalData: {},
            products: {},
            measurements: {},
            currentStep: 0,
            measurementSteps: 2,
            showProductsModal: false,
            showRestoreConfigurationModal: false,
            showCartEditModal: false,
            showPickCartConfigurationModal: false,
            showFieldsInvalidModal: false,
            localStorageKey: "bespoken-configuration",
            localStorageData: null,
            appUrl: this.$backendConfig.settings.appUrl,
            saveStepChangeToURL: true,
            invalidMoreFields: false
        };
    },
    computed: {
        currentStepIsProduct() {
            return this.currentStep < Object.keys(this.products).length;
        },

        currentMeasurementStep() {
            return this.currentStep - Object.keys(this.products).length;
        },

        totalStepsNumber() {
            return Object.keys(this.products).length + this.measurementSteps;
        },

        // Progress bar has different weights for products and measurements steps
        progressBarCurrentStep() {
            let ratio = this.$generalData.progressRatio;

            if (this.currentStepIsProduct)
                return this.currentStep * ratio.products;

            return Object.keys(this.products).length * ratio.products +
                this.currentMeasurementStep * ratio.measurements;
        },

        progressBarTotalSteps() {
            let ratio = this.$generalData.progressRatio;

            return Object.keys(this.products).length * ratio.products +
                (this.measurementSteps - 1) * ratio.measurements;
        },

        // Get products that have not been selected yet
        notSelectedProducts() {
            return Object.values(this.products)
                .map((item, index) => {
                    // Append index to product object to know which step it is located on
                    item.step = index;
                    return item;
                })
                .filter(item => !(this.$db.products[item.id] || (item.id === "contrasts" && (this.$db.products["contrasts-cuff"] || this.$db.products["contrasts-collar"]))));
        },

        // Return measurements categories for shirt or body
        measurementsCategories() {
            if (this.$db.measurements.type)
                return this.measurements.categories[this.$db.measurements.type];
            return {};
        },

        // Number of already selected categories
        configuredProducts() {
            return Object.keys(this.$db.products).length;
        }
    },
    created() {
        this.generalData = this.$backendConfig.general;
        this.products = this.$backendConfig.products;
        this.measurements = this.$backendConfig.measurements;

        // Configuration is not loaded from URL -> see if an abandoned configuration is saved locally
        if (!this.configuredProducts) {
            this.getLocalStorage();

            // Configuration is saved locally
            if (this.localStorageData)
                this.showRestoreConfigurationModal = true;
        }

        if (!this.showRestoreConfigurationModal && this.$cartConfigurations.length) {
            this.showCartEditModal = true;
        }

        // Set listener for page leave
        this.setPageLeaveListener();

        EventBus.$on("invalid-more-fields", (key, value) => {
            if (!key)
                return this.invalidMoreFields = false;

            if (!this.invalidMoreFields)
                this.invalidMoreFields = {};

            this.invalidMoreFields[key] = value;
        });
    },
    mounted() {
        if (this.$db.products.fabrics)
            this.loadButtonHoleFromFabric();

        // Fabric selected event listener
        EventBus.$on("product-selected-fabrics", fabric => {
            this.loadButtonHoleFromFabric(fabric);
        });

        window.onpopstate = ({ state }) => {
            let step;
            if (!state || typeof state !== "object" || !state.hasOwnProperty("step") || (step = parseInt(state.step)) > this.totalStepsNumber) {
                // Invalid state -> get step from URL
                step = this.getStepFromUrl();
            }

            this.setStepFromUrl(step);
        }

        this.setStepFromUrl();
    },
    methods: {
        // Change default button holes colour to fabric colour
        loadButtonHoleFromFabric(fabric = this.$db.products.fabrics) {
            if (!fabric) {
                return;
            }

            let buttonHoleColorExists = false;

            let fabricColor = fabric.properties.color;
            if (Array.isArray(fabricColor))
                fabricColor = fabricColor[0];

            let currentButtonHole = this.$db.products["button-holes"];
            let currentHoleIsDefault = currentButtonHole && currentButtonHole.id === "fabric-color";

            this.products["button-holes"].products.forEach(item => {
                // This button hole is same colour as selected fabric -> set its main image as default button hole image
                if (item.id === fabricColor) {
                    buttonHoleColorExists = true;
                    this.$set(this.products["button-holes"].products[0], "mainImage", item.mainImage);
                    this.$set(this.products["button-holes"].products[0], "preview", item.preview);

                    if (currentHoleIsDefault) {
                        this.$set(this.$db.products["button-holes"], "mainImage", item.mainImage);
                        this.$set(this.$db.products["button-holes"], "preview", item.preview);
                    }
                }
            });

            // No button hole found for this fabric -> unset image
            if (!buttonHoleColorExists) {
                this.$delete(this.products["button-holes"].products[0], "mainImage");
                this.$delete(this.products["button-holes"].products[0], "preview");

                if (currentHoleIsDefault) {
                    this.$delete(this.$db.products["button-holes"], "mainImage");
                    this.$delete(this.$db.products["button-holes"], "preview");
                }
            }
        },

        // Method will run before user leaves page
        beforePageLeave() {
            // Less than 2 categories selected -> don't save to local storage
            if (this.configuredProducts < 2) {
                this.removeLocalStorage();
                return;
            }

            // Save current configuration to local storage
            let configuration = {
                products: this.$db.products,
                measurements: this.$db.measurements,
                note: this.$db.note
            };

            this.setLocalStorage(configuration);
        },

        restoreConfiguration() {
            this.$set(this.$db, "note", this.localStorageData.note);
            this.$set(this.$db, "products", this.localStorageData.products);
            this.$set(this.$db, "measurements", this.localStorageData.measurements);

            this.loadButtonHoleFromFabric();
        },

        setPageLeaveListener() {
            window.onbeforeunload = this.beforePageLeave;
        },

        // Configuration saved to cart
        configurationFinished() {
            // Remove page leave confirmation
            window.onbeforeunload = null;

            // Remove old data from local database
            this.removeLocalStorage();

            window.location.href = this.$settings.cartUrl;
        },

        goToStep(step) {
            if (this.invalidMoreFields) {
                for (let index in this.invalidMoreFields)
                    if (this.invalidMoreFields[index]) {
                        this.showFieldsInvalidModal = true;
                        return false;
                    }
            }

            this.showFieldsInvalidModal = false;
            this.invalidMoreFields = false;
            if (step === '-1')
                this.currentStep--;

            else if (step === '+1')
                this.currentStep++;

            else if (step === 'last')
                this.currentStep = this.totalStepsNumber - 1;
            else
                this.currentStep = step;

            // Measurements begin page is shown but not all products are selectd -> show modal
            if (this.currentMeasurementStep === 0 && this.notSelectedProducts.length)
                this.showProductsModal = true;

            // Listener was removed because shirt is in cart, but user is changing configuration -> reinit listener
            if (!window.onbeforeunload)
                this.setPageLeaveListener();
        },

        getSecondProduct(productId) {
            if (!productId) return null;

            // Clone product object so that filtering doesn't affect original property
            let product = JSON.parse(JSON.stringify(this.products[productId]));


            product.products = product.products
                // Keep just products that should be available as second choice
                .filter(product => product.secondChoice)

                // Remove products' prices & set right defaults
                .map(product => {
                    product.price = null;
                    product.priceInt = null;

                    product.default = product.secondChoiceDefault;

                    return product;
                });

            return product;
        },

        // Select default values for all products that have not been selected yet
        selectDefaults() {
            this.notSelectedProducts.forEach(product => {
                let options = this.products[product.id].products;

                for (let option of options)
                    // Option is default -> choose it
                    if (option.default)
                        return this.$set(this.$db.products, product.id, option);

                // No default option for this product -> pick the first one
                return this.$set(this.$db.products, product.id, options[0]);
            });
        },

        // Local storage facade functions
        setLocalStorage(data) {
            localStorage.setItem(this.localStorageKey, JSON.stringify(data));
        },

        getLocalStorage() {
            let item = localStorage.getItem(this.localStorageKey);
            if (!item)
                return this.localStorageData = null;

            try {
                // Try to parse saved configuration
                this.localStorageData = JSON.parse(item);
            } catch (e) {
                // Stored configuration is corrupt
                this.removeLocalStorage();
                this.localStorageData = null;
            }
        },

        removeLocalStorage() {
            localStorage.removeItem(this.localStorageKey);
        },

        getStepFromUrl() {
            const url = document.location.pathname;
            const split = url.split(this.$tr.stepUrl);

            if (split.length !== 2)
                return 0;

            const step = parseInt(split[1]) || 0;
            return Math.max(step - 1, 0);
        },

        setStepFromUrl(step = null) {
            if (step === null)
                step = this.getStepFromUrl();

            // Temporarily disable saving step changes to URL to prevent duplication
            this.saveStepChangeToURL = false;
            this.currentStep = step;

            this.$nextTick(() => this.saveStepChangeToURL = true);
        },

        // Display a modal to pick one of cart configurations, or redirect to the only configuration in cart
        pickedEditCartShirt() {
            if (this.$cartConfigurations.length === 1) {
                document.location = this.$cartConfigurations[0].url;
                return;
            }

            this.showPickCartConfigurationModal = true;
        },
    },
    watch: {
        currentStep(step) {
            if (!this.saveStepChangeToURL) {
                return;
            }

            const newUrl = this.constructUrl(step);
            history.pushState({step}, undefined, newUrl);
        }
    },
}
</script>
